/**
 * Prompt Diff View
 *
 * Simple line-by-line diff of a prompt override vs its default template.
 * Shows added/removed/unchanged lines with green/red highlighting.
 *
 * @module @humanizer/studio/components/settings/PromptDiffView
 */

// ═══════════════════════════════════════════════════════════════════
// TYPES
// ═══════════════════════════════════════════════════════════════════

interface DiffLine {
  type: 'added' | 'removed' | 'unchanged';
  text: string;
}

interface PromptDiffViewProps {
  defaultTemplate: string;
  overrideTemplate: string;
}

// ═══════════════════════════════════════════════════════════════════
// DIFF ALGORITHM
// ═══════════════════════════════════════════════════════════════════

function computeDiff(oldText: string, newText: string): DiffLine[] {
  const oldLines = oldText.split('\n');
  const newLines = newText.split('\n');
  const result: DiffLine[] = [];

  // Simple LCS-based diff
  const lcs = buildLcsTable(oldLines, newLines);
  let i = oldLines.length;
  let j = newLines.length;
  const stack: DiffLine[] = [];

  while (i > 0 || j > 0) {
    if (i > 0 && j > 0 && oldLines[i - 1] === newLines[j - 1]) {
      stack.push({ type: 'unchanged', text: oldLines[i - 1] });
      i--;
      j--;
    } else if (j > 0 && (i === 0 || lcs[i][j - 1] >= lcs[i - 1][j])) {
      stack.push({ type: 'added', text: newLines[j - 1] });
      j--;
    } else if (i > 0) {
      stack.push({ type: 'removed', text: oldLines[i - 1] });
      i--;
    }
  }

  // Reverse to get correct order
  for (let k = stack.length - 1; k >= 0; k--) {
    result.push(stack[k]);
  }

  return result;
}

function buildLcsTable(a: string[], b: string[]): number[][] {
  const m = a.length;
  const n = b.length;
  const table: number[][] = Array.from({ length: m + 1 }, () =>
    new Array<number>(n + 1).fill(0)
  );

  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) {
        table[i][j] = table[i - 1][j - 1] + 1;
      } else {
        table[i][j] = Math.max(table[i - 1][j], table[i][j - 1]);
      }
    }
  }

  return table;
}

// ═══════════════════════════════════════════════════════════════════
// COMPONENT
// ═══════════════════════════════════════════════════════════════════

export function PromptDiffView({ defaultTemplate, overrideTemplate }: PromptDiffViewProps) {
  const lines = computeDiff(defaultTemplate, overrideTemplate);

  const addedCount = lines.filter((l) => l.type === 'added').length;
  const removedCount = lines.filter((l) => l.type === 'removed').length;

  return (
    <div className="prompt-diff">
      <div className="prompt-diff__header">
        <span className="prompt-diff__stat prompt-diff__stat--added">
          +{addedCount}
        </span>
        <span className="prompt-diff__stat prompt-diff__stat--removed">
          -{removedCount}
        </span>
      </div>
      <pre className="prompt-diff__content">
        {lines.map((line, idx) => (
          <div
            key={idx}
            className={`prompt-diff__line prompt-diff__line--${line.type}`}
          >
            <span className="prompt-diff__prefix">
              {line.type === 'added' ? '+' : line.type === 'removed' ? '-' : ' '}
            </span>
            <span className="prompt-diff__text">
              {line.text || '\u00A0'}
            </span>
          </div>
        ))}
      </pre>
    </div>
  );
}
